import {
  Link,
  Outlet,
  useCanGoBack,
  useLocation,
  useRouter,
} from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';

import { useGetFamilyHub } from '~entities/family';
import { useIsStudioV2 } from '~entities/studios/hooks/use-is-studio-v2';

import { buildFamilyHubTabs } from '~features/family-hub/lib/family-hub-tabs';
import { FamilyHubQuickActions } from '~features/family-hub/ui/family-hub-quick-actions';

import { TabList } from '~shared/components/tab-list';
import { Button } from '~shared/shadcn/ui/button';

/**
 * V2-only Jackrabbit-style family hub (ENR-4054).
 * Classic V1 studios keep `/family?parentId=` and are directed away here.
 */
export const FamilyHubShell = ({ familyId }: { familyId: string }) => {
  const { pathname } = useLocation();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const { isV2, isLoading: isV2Loading } = useIsStudioV2();
  const { data, isLoading, isError } = useGetFamilyHub(
    isV2 ? familyId : undefined,
  );

  if (isV2Loading) {
    return (
      <div className='flex items-center gap-2 p-6 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading studio…
      </div>
    );
  }

  if (!isV2) {
    return (
      <div className='space-y-3 p-6'>
        <p className='text-sm text-muted-foreground'>
          The family hub is available for V2 studios. Open a student from the
          family list instead.
        </p>
        <Button asChild variant='secondary'>
          <Link to='/dashboard'>Back to dashboard</Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 p-6 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading family…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className='p-6 text-sm text-destructive'>
        We could not load this family. Refresh and try again.
      </div>
    );
  }

  const displayName =
    data.family.displayName?.trim() ||
    data.contacts[0]?.parent?.lastName?.trim() ||
    'Family';
  const tabs = buildFamilyHubTabs(familyId, pathname);

  return (
    <>
      <div className='mb-4 flex items-center gap-2'>
        {canGoBack ? (
          <Button
            variant='ghost'
            className='flex items-center p-0 [&_svg]:size-6'
            onClick={() => {
              router.history.back();
            }}
          >
            <CircleArrowLeft className='size-6' />
          </Button>
        ) : (
          <Button
            variant='ghost'
            className='flex items-center p-0 [&_svg]:size-6'
            asChild
          >
            <Link to='/dashboard'>
              <CircleArrowLeft className='size-6' />
            </Link>
          </Button>
        )}
        <h2 className='text-2xl font-bold text-gray-800'>Family</h2>
      </div>

      <div className='px-1 sm:px-2'>
        <h1 className='text-2xl font-semibold leading-9 sm:text-4xl'>
          {displayName}
        </h1>
        <p className='mt-1 text-sm text-muted-foreground'>
          {data.students.length} student
          {data.students.length === 1 ? '' : 's'} · {data.contacts.length}{' '}
          guardian
          {data.contacts.length === 1 ? '' : 's'}
        </p>
      </div>

      {data.family.billingParentId ? (
        <FamilyHubQuickActions
          billingParentId={data.family.billingParentId}
        />
      ) : null}

      <div className='mt-8'>
        <TabList tabList={tabs} />
      </div>
      <div className='m-2 md:m-4 lg:mt-8'>
        <Outlet />
      </div>
    </>
  );
};
